import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixEcomSubscriptionContractsV1SubscriptionContractsUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'www.wixapis.com': [
      {
        srcPath: '/ecom/subscription-contracts/v1/subscription-contracts',
        destPath: '/v1/subscription-contracts',
      },
      {
        srcPath: '/ecom/subscription-contracts/v1/bulk/subscription-contracts',
        destPath: '/v1/bulk/subscription-contracts',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/ecom/subscription-contracts/v1/subscription-contracts',
        destPath: '/v1/subscription-contracts',
      },
      {
        srcPath: '/ecom/subscription-contracts/v1/bulk/subscription-contracts',
        destPath: '/v1/bulk/subscription-contracts',
      },
    ],
    _: [
      {
        srcPath: '/ecom/subscription-contracts/v1/subscription-contracts',
        destPath: '/v1/subscription-contracts',
      },
      {
        srcPath: '/ecom/subscription-contracts/v1/bulk/subscription-contracts',
        destPath: '/v1/bulk/subscription-contracts',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/ecom/subscription-contracts/v1/subscription-contracts',
        destPath: '/v1/subscription-contracts',
      },
      {
        srcPath: '/ecom/subscription-contracts/v1/bulk/subscription-contracts',
        destPath: '/v1/bulk/subscription-contracts',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_ecom_subscription-contracts';

/** Retrieves a SubscriptionContract. */
export function getSubscriptionContract(
  payload: object
): RequestOptionsFactory<any> {
  function __getSubscriptionContract({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.subscription_contracts.v1.subscription_contract',
      method: 'GET' as any,
      methodFqn:
        'wix.ecom.subscription_contracts.v1.SubscriptionContracts.GetSubscriptionContract',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomSubscriptionContractsV1SubscriptionContractsUrl({
        protoPath: '/v1/subscription-contracts/{subscriptionContractId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'subscriptionContract.createdDate' },
              { path: 'subscriptionContract.updatedDate' },
              { path: 'subscriptionContract.subscriptionSettings.startDate' },
              {
                path: 'subscriptionContract.lineItems.image.urlExpirationDate',
              },
              {
                path: 'subscriptionContract.lineItems.digitalFile.expirationDate',
              },
              {
                path: 'subscriptionContract.lineItems.subscriptionInfo.subscriptionSettings.startDate',
              },
              {
                path: 'subscriptionContract.lineItems.serviceProperties.scheduledDate',
              },
              {
                path: 'subscriptionContract.lineItems.serviceProperties.endDate',
              },
              {
                path: 'subscriptionContract.shippingInfo.logistics.deliverByDate',
              },
              {
                path: 'subscriptionContract.shippingInfo.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'subscriptionContract.shippingInfo.logistics.deliveryTimeSlot.to',
              },
              { path: 'subscriptionContract.activities.createdDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'subscriptionContract.lineItems.image.focalPoint.x' },
              { path: 'subscriptionContract.lineItems.image.focalPoint.y' },
              {
                path: 'subscriptionContract.lineItems.physicalProperties.weight',
              },
              {
                path: 'subscriptionContract.billingInfo.address.geocode.latitude',
              },
              {
                path: 'subscriptionContract.billingInfo.address.geocode.longitude',
              },
              {
                path: 'subscriptionContract.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'subscriptionContract.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'subscriptionContract.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'subscriptionContract.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'subscriptionContract.recipientInfo.address.geocode.latitude',
              },
              {
                path: 'subscriptionContract.recipientInfo.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getSubscriptionContract;
}

/**
 * Retrieves a list of SubscriptionContracts, given the provided [paging, filtering, and sorting][1].
 *
 * Up to 300 SubscriptionContracts can be returned per request.
 *
 * To learn how to query SubscriptionContracts, see [API Query Language][2].
 *
 * [1]: https://dev.wix.com/api/rest/getting-started/sorting-and-paging
 * [2]: https://dev.wix.com/api/rest/getting-started/api-query-language
 */
export function querySubscriptionContracts(
  payload: object
): RequestOptionsFactory<any> {
  function __querySubscriptionContracts({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.subscription_contracts.v1.subscription_contract',
      method: 'GET' as any,
      methodFqn:
        'wix.ecom.subscription_contracts.v1.SubscriptionContracts.QuerySubscriptionContracts',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomSubscriptionContractsV1SubscriptionContractsUrl({
        protoPath: '/v1/subscription-contracts/query',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'subscriptionContracts.createdDate' },
              { path: 'subscriptionContracts.updatedDate' },
              { path: 'subscriptionContracts.subscriptionSettings.startDate' },
              {
                path: 'subscriptionContracts.lineItems.image.urlExpirationDate',
              },
              {
                path: 'subscriptionContracts.lineItems.digitalFile.expirationDate',
              },
              {
                path: 'subscriptionContracts.lineItems.subscriptionInfo.subscriptionSettings.startDate',
              },
              {
                path: 'subscriptionContracts.lineItems.serviceProperties.scheduledDate',
              },
              {
                path: 'subscriptionContracts.lineItems.serviceProperties.endDate',
              },
              {
                path: 'subscriptionContracts.shippingInfo.logistics.deliverByDate',
              },
              {
                path: 'subscriptionContracts.shippingInfo.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'subscriptionContracts.shippingInfo.logistics.deliveryTimeSlot.to',
              },
              { path: 'subscriptionContracts.activities.createdDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'subscriptionContracts.lineItems.image.focalPoint.x' },
              { path: 'subscriptionContracts.lineItems.image.focalPoint.y' },
              {
                path: 'subscriptionContracts.lineItems.physicalProperties.weight',
              },
              {
                path: 'subscriptionContracts.billingInfo.address.geocode.latitude',
              },
              {
                path: 'subscriptionContracts.billingInfo.address.geocode.longitude',
              },
              {
                path: 'subscriptionContracts.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'subscriptionContracts.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'subscriptionContracts.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'subscriptionContracts.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'subscriptionContracts.recipientInfo.address.geocode.latitude',
              },
              {
                path: 'subscriptionContracts.recipientInfo.address.geocode.longitude',
              },
            ],
          },
        ]),
      fallback: [
        {
          method: 'POST' as any,
          url: resolveWixEcomSubscriptionContractsV1SubscriptionContractsUrl({
            protoPath: '/v1/subscription-contracts/query',
            data: payload,
            host,
          }),
          data: payload,
        },
      ],
    };

    return metadata;
  }

  return __querySubscriptionContracts;
}
